'use client'

import { useReducedMotion } from 'framer-motion'
import { ArrowRight, Box, ChevronLeft, ChevronRight, Layers } from 'lucide-react'
import Link from 'next/link'
import React, { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react'

import { getDirectionTheme } from '@/lib/directionTheme'

// Helper to map DB category to UI direction
type DirectionSlug = 'development' | 'ai-agents' | 'design' | 'smm' | 'marketing' | 'seo'

function mapCategoryToDirection(cat: string): DirectionSlug {
  const map: Record<string, DirectionSlug> = {
    WEB: 'development',
    AI: 'ai-agents',
    CONTEXT: 'marketing',
    DESIGN: 'design',
    SEO: 'seo',
    SMM: 'smm',
  }
  return map[cat] || 'development'
}

type PromotionData = {
  id: string
  title: string
  description: string
  badge: string
  price: string
  bullets: unknown
  ctaLabel: string
  ctaLink: string
  secLabel: string
  secLink: string
  category: string
}

type Offer = {
  id: string
  direction: DirectionSlug
  badge: string
  title: string
  bullets: string[]
  description: string
  price: string
  cta: { label: string; href: string }
  secondary: { label: string; href: string }
}

// ------------------------------------------------------------------
// Prismatic Card Component
// ------------------------------------------------------------------

const OfferCard = React.memo(function OfferCard({ offer }: { offer: Offer }) {
  const theme = getDirectionTheme(offer.direction)

  // Convert accentRgb to CSS value for reuse
  const accentColor = `rgb(${theme.accentRgb})`

  return (
    <div className="group relative h-full">
      {/* Spotlight / Glow Effect Wrapper */}
      <div className="relative h-full transition-transform duration-500 hover:-translate-y-2">
        {/* 3D Glow behind on Hover - Tinted with Accent */}
        <div
          className="absolute -inset-1 rounded-[42px] opacity-0 blur-xl transition-opacity duration-500 group-hover:opacity-100"
          style={{
            background: `linear-gradient(to bottom, rgba(${theme.accentRgb}, 0.5), transparent)`,
          }}
        />

        {/* Card Container (Border Layer) */}
        <div className="relative h-full overflow-hidden rounded-[40px] border border-white/5 bg-white/[0.03] p-[1px] shadow-2xl backdrop-blur-md transition-all duration-500 group-hover:border-white/20 group-hover:shadow-[0_20px_60px_-15px_rgba(0,0,0,0.7)]">
          {/* Animated Gradient Border on Hover - Tinted with Accent */}
          <div
            className="pointer-events-none absolute inset-0 opacity-50 transition-opacity duration-500 group-hover:opacity-80"
            style={{
              background: `linear-gradient(to bottom right, rgba(${theme.accentRgb}, 0.3), transparent, transparent)`,
            }}
          />

          {/* Inner Content - Transparent Glass */}
          <div className="relative flex h-full flex-col overflow-hidden rounded-[39px] bg-black/40 p-6 backdrop-blur-xl lg:p-8">
            {/* Subtle top sheen - Now Tinted with Accent instead of White */}
            <div
              className="pointer-events-none absolute top-0 right-0 left-0 h-32 opacity-60 transition-opacity duration-500 group-hover:opacity-100"
              style={{
                background: `linear-gradient(to bottom, rgba(${theme.accentRgb}, 0.15), transparent)`,
              }}
            />

            {/* Background blob for deep depth (Top Left) - Amplified */}
            <div
              className="absolute -top-20 -left-20 h-[240px] w-[240px] bg-[radial-gradient(circle,var(--blob-color),transparent_70%)] opacity-40 blur-[60px] transition-all duration-700 group-hover:opacity-70"
              style={
                { '--blob-color': `rgba(${theme.accentRgb}, 0.8)` } as CSSProperties &
                  Record<'--blob-color', string>
              }
            />

            <div className="relative z-10 mb-8 flex hidden items-start justify-between sm:flex">
              <span
                className="inline-flex rounded-xl border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] font-bold tracking-wider text-gray-300 uppercase backdrop-blur-lg transition-colors group-hover:border-white/20 group-hover:bg-white/10"
                style={{ color: accentColor }}
              >
                {offer.badge}
              </span>
              <div className="flex h-10 w-10 items-center justify-center rounded-full border border-white/5 bg-white/[0.02] shadow-inner transition-all duration-300 group-hover:scale-110 group-hover:bg-white/[0.05]">
                <Layers
                  size={18}
                  className="text-white/40 transition-colors group-hover:text-white"
                />
              </div>
            </div>

            {/* Mobile Badge Only */}
            <div className="mb-6 sm:hidden">
              <span
                className="inline-flex rounded-xl border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] font-bold tracking-wider text-gray-300 uppercase backdrop-blur-lg"
                style={{ color: accentColor }}
              >
                {offer.badge}
              </span>
            </div>

            <h3 className="home-card-title relative z-10 mb-2 line-clamp-2 flex min-h-[4.2rem] items-start leading-[1.12] tracking-tight text-white">
              {offer.title}
            </h3>

            {/* Description Field */}
            <p className="home-subnote relative z-10 mb-6 line-clamp-3 flex min-h-[5.4rem] items-start overflow-hidden text-gray-400">
              {offer.description}
            </p>

            <div className="relative z-10 mb-8 font-mono text-[clamp(1.5rem,2.2vw,2rem)] font-light tracking-tighter text-white/50 transition-colors group-hover:text-white/80">
              {offer.price}
            </div>

            <div className="relative z-10 mb-8 flex-1 space-y-3">
              {offer.bullets.slice(0, 3).map((bull, i) => (
                <div
                  key={i}
                  className="group/list flex items-start gap-3 border-b border-white/[0.04] py-2 last:border-0"
                >
                  <Box
                    size={14}
                    className="mt-1 shrink-0 text-white/20 transition-colors group-hover/list:text-white/60"
                  />
                  <span className="home-body leading-tight font-medium text-gray-400 transition-colors group-hover/list:text-gray-200">
                    {bull}
                  </span>
                </div>
              ))}
            </div>

            <Link
              href={offer.cta.href}
              className="group/btn relative mt-auto flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl border border-white/10 bg-white/5 py-4 text-center font-bold text-white shadow-[0_10px_30px_rgba(0,0,0,0.3)] transition-all duration-300 hover:border-white hover:bg-white hover:text-black"
            >
              <span className="home-meta relative z-10 flex items-center gap-2 whitespace-nowrap">
                {offer.cta.label}
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover/btn:translate-x-1"
                />
              </span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
})

// ------------------------------------------------------------------
// Main Component
// ------------------------------------------------------------------

interface PromotionsProps {
  initialPromotions?: PromotionData[]
}

export default function Promotions({ initialPromotions = [] }: PromotionsProps) {
  const reduceMotion = Boolean(useReducedMotion())
  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const itemRefs = useRef<Array<HTMLDivElement | null>>([])
  const [active, setActive] = useState(0)

  // Drag State
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)

  // Drag Handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollerRef.current) return
    setIsDragging(true)
    setStartX(e.pageX - scrollerRef.current.offsetLeft)
    setScrollLeft(scrollerRef.current.scrollLeft)
  }

  const handleMouseLeave = () => {
    setIsDragging(false)
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollerRef.current) return
    e.preventDefault()
    const x = e.pageX - scrollerRef.current.offsetLeft
    const walk = (x - startX) * 1.5 // Scroll speed multiplier
    scrollerRef.current.scrollLeft = scrollLeft - walk
  }

  const offers: Offer[] = useMemo(() => {
    if (initialPromotions.length > 0) {
      return initialPromotions.map((p) => {
        let bullets: string[] = []
        try {
          bullets = Array.isArray(p.bullets)
            ? p.bullets
            : typeof p.bullets === 'string'
              ? JSON.parse(p.bullets)
              : []
        } catch {
          bullets = []
        }

        return {
          id: p.id,
          direction: mapCategoryToDirection(p.category),
          badge: p.badge,
          title: p.title,
          bullets: bullets,
          description: p.description,
          price: p.price,
          cta: { label: p.ctaLabel, href: p.ctaLink },
          secondary: { label: p.secLabel, href: p.secLink },
        }
      })
    }

    return [
      {
        id: 'offer-dev-landing',
        direction: 'development',
        badge: '7 дней',
        title: 'Сайт-воронка',
        bullets: ['Копирайт + структура', 'Форма + Telegram', 'Аналитика'],
        description: 'Запуск продаж.',
        price: '20 000 ₽',
        cta: { label: 'Обсудить', href: '/#contact-form' },
        secondary: { label: 'Услуги', href: '/services/development' },
      },
      {
        id: 'offer-smm-vk',
        direction: 'smm',
        badge: 'Месяц',
        title: 'SMM ВКонтакте',
        bullets: ['30 постов', 'Оформление', 'Таргет'],
        description: 'Полный месяц работы.',
        price: '28 000 ₽',
        cta: { label: 'Забрать', href: '/#contact-form' },
        secondary: { label: 'Маркетинг', href: '/services/smm' },
      },
      {
        id: 'offer-ai-pilot',
        direction: 'ai-agents',
        badge: 'Пилот',
        title: 'AI-помощник',
        bullets: ['База знаний', 'Интеграции CRM', 'Метрики качества'],
        description: 'Быстрый пилот.',
        price: 'от 150к ₽',
        cta: { label: 'Запросить', href: '/#contact-form' },
        secondary: { label: 'Про AI', href: '/services/ai-agents' },
      },
      {
        id: 'offer-seo-audit',
        direction: 'seo',
        badge: 'Аудит',
        title: 'SEO Стратегия',
        bullets: ['Технический аудит', 'Семантика', 'План на 3 мес.'],
        description: 'Полный разбор.',
        price: '15 000 ₽',
        cta: { label: 'Заказать', href: '/#contact-form' },
        secondary: { label: 'SEO Услуги', href: '/services/seo' },
      },
      {
        id: 'offer-context-ads',
        direction: 'marketing',
        badge: 'Трафик',
        title: 'Контекст',
        bullets: ['Яндекс.Директ', 'Минус-слова', 'Ретаргетинг'],
        description: 'Быстрый запуск.',
        price: '25 000 ₽',
        cta: { label: 'Запустить', href: '/#contact-form' },
        secondary: { label: 'Реклама', href: '/services/marketing' },
      },
      {
        id: 'offer-design-system',
        direction: 'design',
        badge: 'Система',
        title: 'UI-кит',
        bullets: ['Библиотека', 'Гайдлайны', 'Иконки'],
        description: 'Системный подход.',
        price: '40 000 ₽',
        cta: { label: 'Обсудить', href: '/#contact-form' },
        secondary: { label: 'Дизайн', href: '/services/design' },
      },
    ] as Offer[]
  }, [initialPromotions])

  // Scroll Active State Logic
  const handleScroll = () => {
    if (!scrollerRef.current) return
    const scrollLeft = scrollerRef.current.scrollLeft

    // Find item closest to the left edge
    let closestIndex = 0
    let minDiff = Infinity

    itemRefs.current.forEach((el, index) => {
      if (!el) return
      const diff = Math.abs(el.offsetLeft - (scrollerRef.current?.offsetLeft || 0) - scrollLeft)
      // Small tolerance or bias to left
      if (diff < minDiff) {
        minDiff = diff
        closestIndex = index
      }
    })

    if (active !== closestIndex) {
      setActive(closestIndex)
    }
  }

  useEffect(() => {
    // Reset to first slide on mount (prevents partial second-card peek on mobile)
    if (scrollerRef.current) {
      scrollerRef.current.scrollLeft = 0
    }
  }, [])

  function scrollTo(i: number) {
    const el = itemRefs.current[i]
    if (!el) return
    el.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      inline: 'start',
      block: 'nearest',
    })
  }

  // Linear Navigation Logic (No Looping)

  function canGoPrev() {
    return active > 0
  }

  function canGoNext() {
    // Allow scrolling until valid range
    return active < offers.length - 1
  }

  function prev() {
    if (canGoPrev()) {
      scrollTo(active - 1)
    }
  }

  function next() {
    if (canGoNext()) {
      scrollTo(active + 1)
    }
  }

  return (
    <section className="home-section relative z-10 overflow-hidden">
      <div className="home-container relative z-10">
        <div className="mb-12 flex items-end justify-between gap-6">
          {/* Header Content */}
          <div className="max-w-3xl">
            <div className="home-kicker mb-6 inline-flex items-center rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-gray-400 shadow-lg backdrop-blur-md">
              Prismatic Flow
            </div>
            <h2 className="home-title mb-6 text-white">
              Готовые решения <br className="hidden sm:block" />
              <span className="animate-gradient-x bg-gradient-to-r from-purple-400 via-blue-400 to-indigo-400 bg-clip-text text-transparent">
                для вашего бизнеса
              </span>
            </h2>
            <p className="home-lead max-w-xl text-gray-400">
              Выберите пакет под вашу задачу. Всё включено — запускаем в течение 7 дней.
            </p>
          </div>

          {/* Navigation Buttons */}
          <div className="hidden items-center gap-3 pb-2 md:flex">
            <button
              onClick={prev}
              disabled={!canGoPrev()}
              className={`group flex h-14 w-14 items-center justify-center rounded-full border bg-white/5 transition-all ${!canGoPrev() ? 'cursor-not-allowed border-white/5 opacity-30' : 'cursor-pointer border-white/10 hover:border-white/20 hover:bg-white/10'}`}
              aria-label="Предыдущая акция"
            >
              <ChevronLeft
                size={24}
                className={`transition-colors ${!canGoPrev() ? 'text-white/30' : 'text-white/60 group-hover:text-white'}`}
              />
            </button>
            <button
              onClick={next}
              disabled={!canGoNext()}
              className={`group flex h-14 w-14 items-center justify-center rounded-full border bg-white/5 transition-all ${!canGoNext() ? 'cursor-not-allowed border-white/5 opacity-30' : 'cursor-pointer border-white/10 hover:border-white/20 hover:bg-white/10'}`}
              aria-label="Следующая акция"
            >
              <ChevronRight
                size={24}
                className={`transition-colors ${!canGoNext() ? 'text-white/30' : 'text-white/60 group-hover:text-white'}`}
              />
            </button>
          </div>
        </div>

        {/* Carousel */}
        <div
          ref={scrollerRef}
          className={`no-scrollbar mx-0 flex flex-nowrap items-stretch overflow-x-auto pb-12 transition-all duration-300 sm:-mx-3 ${
            isDragging ? 'cursor-grabbing snap-none' : 'cursor-grab snap-x snap-mandatory'
          }`}
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseLeave}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          onScroll={handleScroll}
        >
          {offers.map((o, i) => (
            <div
              key={o.id}
              ref={(el) => {
                itemRefs.current[i] = el
              }}
              className="flex w-full flex-none snap-start px-0 py-4 select-none sm:w-1/2 sm:px-3 xl:w-1/3"
            >
              <div className={`h-full w-full ${isDragging ? 'pointer-events-none' : ''}`}>
                <OfferCard offer={o} />
              </div>
            </div>
          ))}
        </div>

        {/* Dots. Hit area is padded to a ~44px touch target (Lighthouse's
            tap-target-size audit) without changing the visible pill size;
            the container drops its own gap since the padding already
            spaces the targets out. */}
        <div className="mt-4 flex justify-center">
          {offers.map((o, i) => {
            const isActive = i === active
            return (
              <button
                key={o.id}
                onClick={() => scrollTo(i)}
                aria-label={`Перейти к акции ${i + 1}`}
                aria-current={isActive ? 'true' : undefined}
                className="p-[19px]"
              >
                <span
                  className={[
                    'block h-1.5 rounded-full transition-all duration-500',
                    isActive
                      ? 'w-12 bg-white shadow-[0_0_10px_white]'
                      : 'w-1.5 bg-white/20 hover:bg-white/40',
                  ].join(' ')}
                />
              </button>
            )
          })}
        </div>
      </div>
    </section>
  )
}
